import { IOnStreetReservationResponse, IVehicleReservationResponse } from '../dtos';

/**
 * Normalized booking model for UI consumption
 */
export interface IBookingViewModel extends IOnStreetReservationResponse {
  vehicles: IVehicleReservationResponse[];
  rebook: boolean;
  location: string;
  dateTime: string;
  timeRemaining: string;
}

/**
 * Maps an on-street reservation response from the API into a UI-friendly booking view model
 * Normalizes vehicle/vehicles field, computes derived fields for templates
 */
export function mapReservationToBooking(
  reservation: IOnStreetReservationResponse,
  currentLang: string = 'en'
): IBookingViewModel {
  // Normalize vehicles array - backend sends 'vehicle' (singular), UI expects 'vehicles' (plural)
  const vehicles: IVehicleReservationResponse[] = reservation.vehicle
    ? [reservation.vehicle]
    : [];

  // Determine if this is a rebook scenario (expired/completed/cancelled bookings)
  const normalizedStatus = normalizeStatus(reservation.status);
  const rebook =
    normalizedStatus === 'Expired' ||
    normalizedStatus === 'Completed' ||
    normalizedStatus === 'Cancelled';

  // Extract location from parkingArea
  const location =
    currentLang === 'ar'
      ? reservation.parkingArea?.arabicName || ''
      : reservation.parkingArea?.englishName || '';

  // Format dateTime for display (used in rebook cards)
  const dateTime = formatDateTime(reservation.startTime);

  // Calculate time remaining in HH:mm:ss format for countdown timer
  const timeRemaining = calculateTimeRemaining(reservation.endTime);

  return {
    ...reservation,
    vehicles,
    status: normalizedStatus,
    rebook,
    location,
    dateTime,
    timeRemaining
  };
}

/**
 * Normalizes status strings to match UI expectations
 * Backend might send: "Active", "Expired", "AboutToExpire", etc.
 * UI expects: "Active", "Expired", "About to expire", etc.
 */
function normalizeStatus(status: string): string {
  if (!status) return 'Active';

  // Map known backend status values to UI-friendly labels
  const statusMap: Record<string, string> = {
    'Active': 'Active',
    'Expired': 'Expired',
    'Completed': 'Completed',
    'Cancelled': 'Cancelled',
    'AboutToExpire': 'About to expire',
    'About to expire': 'About to expire'
  };

  return statusMap[status] || status;
}

/**
 * Formats a date/time string for display
 * Format: "DD MMM, YYYY HH:mm AM/PM"
 */
function formatDateTime(dateValue: Date | string | null | undefined): string {
  if (!dateValue) return 'NA';

  const date = new Date(dateValue);

  // Format date
  const day = date.toLocaleDateString('en-GB', { day: '2-digit' });
  const month = date.toLocaleDateString('en-GB', { month: 'short' });
  const year = date.toLocaleDateString('en-GB', { year: 'numeric' });

  // Format time
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, '0');

  const time = `${formattedHours}:${formattedMinutes} ${period}`;

  return `${day} ${month}, ${year} ${time}`;
}

/**
 * Calculates time remaining until the given end time
 * Returns formatted string in HH:mm:ss format for countdown timer
 */
function calculateTimeRemaining(endTime: Date | string | null | undefined): string {
  if (!endTime) return '00:00:00';

  const end = new Date(endTime);
  const now = new Date();
  const diffMs = end.getTime() - now.getTime();

  // If time has passed, return 00:00:00
  if (diffMs <= 0) return '00:00:00';

  // Convert to hours, minutes, seconds
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // Format as HH:mm:ss
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}
